import React, { useState, useEffect } from 'react';
import { Crown, Check, CreditCard, Clock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useSubscription, SUBSCRIPTION_TIERS, SubscriptionTier } from '@/hooks/useSubscription';
import { useCredits } from '@/hooks/useCredits';

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: 'video-tools' | 'reader' | 'general' | 'ai_feature';
  defaultTier?: SubscriptionTier;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  open,
  onOpenChange,
  trigger = 'general',
  defaultTier
}) => {
  const { startSubscription, loading, canUseTools, isSubscriber, tierName } = useSubscription();
  const { credits, hasCredits, fetchBalance } = useCredits();
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>(defaultTier || 'monthly');
  const [upgrading, setUpgrading] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');

  // Load the credit balance when the modal opens so we can tell credit-holders
  // their credits are inert without an active membership.
  useEffect(() => { if (open) fetchBalance(); }, [open, fetchBalance]);

  const handleUpgrade = async (tier: SubscriptionTier) => {
    setUpgrading(true);
    try {
      await startSubscription(tier);
    } finally {
      setUpgrading(false);
    }
  };

  // Get contextual message based on trigger
  const getContextMessage = () => {
    switch (trigger) {
      case 'video-tools':
        return 'Files over 10MB require a Hey Joe Plus subscription.';
      case 'ai_feature':
        return 'This feature requires a Hey Joe Plus subscription.';
      case 'reader':
        return 'Reader sessions require a Hey Joe Plus subscription.';
      default:
        return 'Choose the plan that works for you.';
    }
  };

  // Don't show the upgrade modal to users who already have tool access. Key off
  // canUseTools, NOT isSubscriber: the free Creator/Reader tiers are
  // isSubscriber:true but canUseTools:false, and using isSubscriber here made the
  // modal silently no-op for them (button click did nothing).
  if (canUseTools) {
    return null;
  }

  const plusTier = billingInterval === 'yearly' ? SUBSCRIPTION_TIERS.yearly : SUBSCRIPTION_TIERS.monthly;

  const tiers = [
    {
      ...SUBSCRIPTION_TIERS.day_pass,
      icon: Clock,
      popular: false,
      buttonText: 'Get Day Pass'
    },
    {
      ...plusTier,
      icon: Crown,
      popular: true,
      buttonText: billingInterval === 'yearly' ? 'Subscribe Yearly' : 'Subscribe'
    }
    // Creator (free, sell-only) intentionally omitted — this modal unlocks PAID
    // tools/reader features; selling on the marketplace lives at /plans + /my-store.
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto"
        style={{
          background: 'var(--hj-surface-1)',
          border: '1px solid var(--hj-hairline)',
          color: 'var(--hj-ink)',
        }}
      >
        <DialogHeader style={{ gap: 10 }}>
          <div
            className="inline-flex items-center gap-2 mono-label self-start"
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              background: 'var(--hj-yellow-tint)',
              color: 'var(--hj-yellow-ink)',
              fontWeight: 700,
            }}
          >
            <Crown className="h-3.5 w-3.5" />
            Hey Joe+
          </div>
          <DialogTitle asChild>
            <h2
              className="font-hj-display m-0"
              style={{
                fontSize: 'clamp(28px, 3.6vw, 36px)',
                lineHeight: 1.02,
                color: 'var(--hj-ink)',
                letterSpacing: '-0.03em',
                fontWeight: 800,
                marginTop: 4,
              }}
            >
              Pick your{' '}
              <span
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  padding: '0.04em 0.18em',
                  background: 'var(--hj-yellow)',
                  color: 'oklch(0.18 0.005 70)',
                  clipPath: 'polygon(2% 8%, 98% 0%, 100% 92%, 0% 100%)',
                  transform: 'rotate(-1.5deg)',
                }}
              >
                plan.
              </span>
            </h2>
          </DialogTitle>
          <DialogDescription asChild>
            <p
              className="m-0"
              style={{ marginTop: 6, fontSize: 14, color: 'var(--hj-ink)', lineHeight: 1.5 }}
            >
              {getContextMessage()}
            </p>
          </DialogDescription>

          {hasCredits && (
            <p
              className="m-0"
              style={{
                marginTop: 10,
                padding: '10px 12px',
                borderRadius: 8,
                background: 'var(--hj-yellow)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 13.5,
                fontWeight: 600,
                lineHeight: 1.45,
              }}
            >
              {isSubscriber
                ? <>You have {credits} video token{credits === 1 ? '' : 's'}, but your {tierName || 'current'} plan doesn’t include the video tools, so they can’t be spent yet. Upgrade to Hey Joe Plus and they’re ready to go.</>
                : <>You have {credits} video token{credits === 1 ? '' : 's'} on your account. Video tokens can only be used with an active Hey Joe Plus membership. Subscribe and they’re ready to go.</>}
            </p>
          )}
        </DialogHeader>

        {/* Billing Toggle */}
        <div className="flex justify-center" style={{ marginTop: 8 }}>
          <div
            className="inline-flex items-center gap-1"
            style={{
              padding: 4,
              borderRadius: 999,
              background: 'var(--hj-surface-2)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            <TogglePill active={billingInterval === 'monthly'} onClick={() => setBillingInterval('monthly')}>
              Monthly
            </TogglePill>
            <TogglePill active={billingInterval === 'yearly'} onClick={() => setBillingInterval('yearly')}>
              Yearly
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  fontWeight: 700,
                  color: billingInterval === 'yearly' ? 'oklch(0.18 0.005 70)' : 'var(--hj-green)',
                }}
              >
                Save 17%
              </span>
            </TogglePill>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" style={{ marginTop: 14 }}>
          {tiers.map((tier) => {
            const Icon = tier.icon;
            return (
              <div
                key={tier.id}
                className="relative flex flex-col cursor-pointer transition-colors"
                onClick={() => setSelectedTier(tier.id)}
                style={{
                  padding: 18,
                  borderRadius: 'var(--hj-r-md)',
                  background: tier.popular ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                  border: `1px solid ${tier.popular ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
                }}
              >
                {tier.popular && (
                  <span
                    className="mono-label absolute"
                    style={{
                      top: -10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: 'var(--hj-blue)',
                      color: 'oklch(0.18 0.005 70)',
                      fontWeight: 700,
                    }}
                  >
                    Most popular
                  </span>
                )}

                <div className="text-center" style={{ marginBottom: 14, paddingTop: 4 }}>
                  <Icon
                    className="h-7 w-7 mx-auto"
                    style={{
                      color: tier.popular ? 'var(--hj-blue)' : 'var(--hj-ink-3)',
                      marginBottom: 8,
                    }}
                  />
                  <h3
                    className="font-hj-display m-0"
                    style={{ fontSize: 17, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}
                  >
                    {tier.id === 'yearly' ? 'Hey Joe Plus' : tier.name}
                  </h3>
                  <div style={{ marginTop: 8 }}>
                    <span
                      className="tabular"
                      style={{
                        fontSize: 28,
                        fontWeight: 800,
                        color: 'var(--hj-ink)',
                        fontFamily: 'var(--hj-font-mono)',
                      }}
                    >
                      {tier.priceDisplay}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--hj-ink-3)' }}> /{tier.period}</span>
                  </div>
                  {'monthlyEquivalent' in tier && (
                    <p
                      className="m-0"
                      style={{ marginTop: 4, fontSize: 11, color: 'var(--hj-green)', fontWeight: 600 }}
                    >
                      {tier.monthlyEquivalent} — save $20
                    </p>
                  )}
                </div>

                <p
                  className="text-center m-0"
                  style={{ fontSize: 12.5, color: 'var(--hj-ink)', lineHeight: 1.5, marginBottom: 14 }}
                >
                  {tier.description}
                </p>

                <ul
                  className="flex-1 flex flex-col gap-1.5"
                  style={{ paddingLeft: 0, listStyle: 'none', margin: 0, marginBottom: 14 }}
                >
                  {tier.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2"
                      style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.45 }}
                    >
                      <Check className="h-3.5 w-3.5 flex-shrink-0" style={{ color: 'var(--hj-green)', marginTop: 2 }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpgrade(tier.id);
                  }}
                  disabled={loading || upgrading}
                  className="inline-flex items-center justify-center disabled:cursor-not-allowed"
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: 999,
                    background: tier.popular ? 'var(--hj-blue)' : 'transparent',
                    border: `1px solid ${tier.popular ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
                    color: tier.popular ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink)',
                    fontSize: 13,
                    fontWeight: tier.popular ? 700 : 500,
                    fontFamily: 'inherit',
                    cursor: loading || upgrading ? 'not-allowed' : 'pointer',
                    opacity: loading || upgrading ? 0.6 : 1,
                    minHeight: 40,
                  }}
                >
                  {upgrading && selectedTier === tier.id ? 'Redirecting…' : tier.buttonText}
                </button>
              </div>
            );
          })}
        </div>

        {/* Trust Indicators */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2"
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: '1px solid var(--hj-hairline-soft)',
            fontSize: 11.5,
            color: 'var(--hj-ink-3)',
          }}
        >
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-3 w-3" style={{ color: 'var(--hj-green)' }} />
            Cancel anytime
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CreditCard className="h-3 w-3" />
            Secure Stripe checkout
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TogglePill: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center transition-all"
    style={{
      padding: '6px 14px',
      borderRadius: 999,
      background: active ? 'var(--hj-blue)' : 'transparent',
      color: active ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink-2)',
      fontSize: 12,
      fontWeight: active ? 700 : 500,
      fontFamily: 'inherit',
      cursor: 'pointer',
      border: 0,
    }}
  >
    {children}
  </button>
);

export default UpgradeModal;
